import { observable, action, computed, runInAction } from "mobx";
import agent from "../agent";
import userStore from "./userStore";
import profileStore from "./profileStore";
import templateStore from "./templateStore";
import generalProfileStore from "./generalProfileStore";
import { Promise } from "bluebird";
import { ProfileType } from "../types/enum";
import { Storage } from "aws-amplify";

class DocumentsProfileStore {
    @observable inProgress = false;
    @observable errors = undefined;
    @observable profileId = undefined;
    @observable type = 0;

    @observable documents = [];
    @observable documentUploadInProgress = false;

    @observable insurancePolicyHolder = "";
    @observable insuranceCompany = "";
    @observable insurancePolicyNumber = "";
    @observable insuranceExpiryDate = undefined;
    @observable insuranceFileGuid = undefined;
    @observable insuranceFileName = undefined;
    @observable insuranceFileContent = undefined;
    @observable insuranceFileContentUrl = undefined;

    @observable addDocumentModalOpen = false;
    @observable editingDocument = null;

    @computed get insuranceDocument() {
        return this.documents.find(doc => doc.documentType === 'insurance');
    }

    @computed get generalDocuments() {
        return this.documents.filter(doc => doc.documentType === 'general');
    }

    @action
    loadInitialData(profileId) {
        const { currentUser } = userStore;
        const { id } = generalProfileStore;
        const { currentProfile } = profileStore;

        profileId = profileId ? profileId : currentProfile ? currentProfile.id : id;
        this.profileId = profileId;

        if (currentUser) {
            this.inProgress = true;

            const loadInsuranceProfile = agent.MusicianInsuranceProfile.load(profileId)
                .then(
                    action(profile => {
                        if (!profile) throw new Error("Can't load profile");
                        this.insurancePolicyHolder = profile.insurancePolicyHolder || "";
                        this.insuranceCompany = profile.insuranceCompany || "";
                        this.insurancePolicyNumber = profile.insurancePolicyNumber || "";
                        this.insuranceExpiryDate = profile.insuranceExpiryDate;
                        this.insuranceFileGuid = profile.insuranceFileGuid;
                        this.insuranceFileName = profile.insuranceFileName;
                        this.insuranceFileContent = profile.insuranceFileContent;
                        this.insuranceFileContentUrl = profile.insuranceFileContentUrl;
                    })
                );

            const loadDocuments = agent.Document.getByProfile(profileId)
                .then(
                    action(response => {
                        this.documents = [];
                        const docs = response && response.documents ? response.documents : (Array.isArray(response) ? response : []);
                        docs.forEach(doc => {
                            const mappedDoc = {
                                id: doc.guid,
                                title: doc.title || (doc.documentType === 'insurance' ? "Public Liability Insurance" : doc.fileName),
                                documentType: doc.documentType,
                                fileName: doc.fileName,
                                fileContent: doc.fileContent,
                                fileContentUrl: doc.fileContentUrl || doc.fileContent,
                                fileGuid: doc.guid,
                                mime: doc.mime,
                                createdAt: doc.createdAt
                            };
                            this.documents.push(mappedDoc);

                            if (doc.documentType === 'insurance') {
                                this.insuranceFileGuid = doc.guid;
                                this.insuranceFileName = doc.fileName;
                                this.insuranceFileContent = doc.fileContent;
                                this.insuranceFileContentUrl = doc.fileContentUrl || doc.fileContent;
                            }
                        });
                    })
                )
                .catch(action(err => {
                    console.error("Error loading documents:", err);
                    this.documents = [];
                }));

            return Promise.all([loadInsuranceProfile, loadDocuments])
                .finally(
                    action(() => {
                        this.inProgress = false;
                        templateStore.hideLoadingScreen();
                    })
                );
        } else {
            return Promise.resolve();
        }
    }

    @action
    reset() {
        this.insurancePolicyHolder = "";
        this.insuranceCompany = "";
        this.insurancePolicyNumber = "";
        this.insuranceExpiryDate = undefined;
        this.insuranceFileGuid = undefined;
        this.insuranceFileName = undefined;
        this.documents = [];
    }

    @action
    setInsurancePolicyHolder(insurancePolicyHolder) {
        this.insurancePolicyHolder = insurancePolicyHolder;
    }

    @action
    setInsuranceCompany(insuranceCompany) {
        this.insuranceCompany = insuranceCompany;
    }

    @action
    setInsurancePolicyNumber(insurancePolicyNumber) {
        this.insurancePolicyNumber = insurancePolicyNumber;
    }

    @action
    setInsuranceExpiryDate(insuranceExpiryDate) {
        this.insuranceExpiryDate = insuranceExpiryDate;
    }

    @action
    openAddDocumentModal(document = null) {
        this.editingDocument = document;
        this.addDocumentModalOpen = true;
    }

    @action
    closeAddDocumentModal() {
        this.editingDocument = null;
        this.addDocumentModalOpen = false;
    }

    async uploadDocument(title, documentType, file, insuranceDetails = {}) {
        runInAction(() => {
            this.documentUploadInProgress = true;
        });

        try {
            const isInsurance = documentType === 'insurance' || documentType === 1;
            const storagePath = isInsurance
                ? `document/insurance/${this.profileId}/${file.name}`
                : `document/custom/${this.profileId}/${file.name}`;
            
            const uploadResult = await Storage.put(storagePath, file);

            const docPayload = [{
                fileContent: uploadResult.key,
                fileName: file.name,
                mime: file.type,
                title: title,
                documentType: isInsurance ? 1 : 2
            }];

            await agent.Document.save(this.profileId, docPayload);
            await this.loadDocuments();

            runInAction(() => {
                if (isInsurance) {
                    const insuranceDoc = this.documents.find(d => d.documentType === 'insurance');
                    if (insuranceDoc) {
                        this.insuranceFileGuid = insuranceDoc.fileGuid;
                        this.insuranceFileName = insuranceDoc.fileName;
                        this.insuranceFileContent = insuranceDoc.fileContent;
                    }
                }
                this.closeAddDocumentModal();
            });

            return { success: true };
        } catch (error) {
            console.error("Error uploading document:", error);
            return { success: false, error: error.message };
        } finally {
            runInAction(() => {
                this.documentUploadInProgress = false;
            });
        }
    }

    async loadDocuments() {
        try {
            const response = await agent.Document.getByProfile(this.profileId);
            const docs = response && response.documents ? response.documents : (Array.isArray(response) ? response : []);
            runInAction(() => {
                this.documents = docs.map(doc => {
                    if (doc.documentType === 'insurance') {
                        this.insuranceFileGuid = doc.guid;
                        this.insuranceFileName = doc.fileName;
                        this.insuranceFileContent = doc.fileContent;
                        this.insuranceFileContentUrl = doc.fileContentUrl || doc.fileContent;
                    }
                    return {
                        id: doc.guid,
                        title: doc.title || (doc.documentType === 'insurance' ? "Public Liability Insurance" : doc.fileName),
                        documentType: doc.documentType,
                        fileName: doc.fileName,
                        fileContent: doc.fileContent,
                        fileContentUrl: doc.fileContentUrl || doc.fileContent,
                        fileGuid: doc.guid,
                        mime: doc.mime,
                        createdAt: doc.createdAt
                    };
                });
            });
        } catch (error) {
            console.error("Error loading documents:", error);
        }
    }

    async downloadInsuranceFile() {
        const insuranceDoc = this.documents.find(d => d.documentType === 'insurance');
        if (insuranceDoc) {
            return this.downloadDocument(insuranceDoc);
        } else if (this.insuranceFileContent || this.insuranceFileContentUrl) {
            try {
                const url = await Storage.get(this.insuranceFileContentUrl || this.insuranceFileContent);
                window.open(url);
                return { success: true };
            } catch (error) {
                console.error("Error downloading insurance file:", error);
                return { success: false, error: error.message };
            }
        }
    }

    async deleteInsuranceFile() {
        const insuranceDoc = this.documents.find(d => d.documentType === 'insurance');
        if (insuranceDoc) {
            return this.deleteDocument(insuranceDoc);
        }
    }

    async deleteDocument(document) {
        try {
            if (document.fileContent) {
                await Storage.remove(document.fileContent);
            }

            if (document.fileGuid || document.id) {
                await agent.Document.delete(document.fileGuid || document.id);
            }

            runInAction(() => {
                if (document.documentType === 'insurance') {
                    this.insuranceFileGuid = undefined;
                    this.insuranceFileName = "";
                    this.insuranceFileContent = undefined;
                }
                this.documents = this.documents.filter(d => d.id !== document.id);
            });

            return { success: true };
        } catch (error) {
            console.error("Error deleting document:", error);
            return { success: false, error: error.message };
        }
    }

    async downloadDocument(document) {
        try {
            const url = await Storage.get(document.fileContentUrl || document.fileContent);
            window.open(url);
            return { success: true };
        } catch (error) {
            console.error("Error downloading document:", error);
            return { success: false, error: error.message };
        }
    }

    @action
    setType(type) {
        this.type = type;
    }

    @action
    submit(profileId, insuranceFile) {
        this.inProgress = true;
        this.errors = undefined;
        const user = userStore.currentUser;
        const profile = {
            id: profileId ? profileId : this.profileId,
            userId: user.id,
            insurancePolicyHolder: this.insurancePolicyHolder,
            insuranceCompany: this.insuranceCompany,
            insurancePolicyNumber: this.insurancePolicyNumber,
            insuranceExpiryDate: this.insuranceExpiryDate,
            type: this.type
        };

        return this.updateProfile(profile)
            .then(
                action(async resp => {
                    if (insuranceFile) {
                        await this.uploadDocument(
                            "Public Liability Insurance",
                            'insurance',
                            insuranceFile,
                            {}
                        );
                    }
                    return resp;
                })
            )
            .catch(
                action(err => {
                    this.errors = err.response && err.response.data.errors;
                    throw err;
                })
            )
            .finally(
                action(() => {
                    this.inProgress = false;
                })
            );
    }

    @action
    updateProfile(data) {
        switch (this.type) {
            case 1:
                return agent.MusicianInsuranceProfile.update(data).then(profile => {
                    return profile;
                });
            case 2:
                return agent.AgentInsuranceProfile.update(data).then(profile => {
                    return profile;
                });
            default:
                return agent.MusicianInsuranceProfile.update(data).then(profile => {
                    return profile;
                });
        }
    }
}

export default new DocumentsProfileStore();
